// components/home/WhyChooseUs.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaUserMd, 
  FaHospital, 
  FaHeartbeat, 
  FaHandHoldingHeart, 
  FaShieldAlt, 
  FaClock,
  FaMoneyBillWave,
  FaAward,
  FaUsers,
  FaStethoscope,
  FaAmbulance,
  FaFileMedical,
  FaGlobeAmericas,
  FaCogs,
  FaChartLine,
  FaUserCheck,
  FaRegCalendarCheck,
  FaRegStar,
  FaRegSmile
} from 'react-icons/fa';
import { GiMedicines, GiHealing } from 'react-icons/gi';
import { MdHealthAndSafety, MdFamilyRestroom } from 'react-icons/md';
import { TbCertificate, TbClock24 } from 'react-icons/tb';

const WhyChooseUs = () => {
    const [activeTab, setActiveTab] = useState('expertise');

    const tabs = [
        { id: 'expertise', label: 'Medical Expertise', icon: <FaUserMd /> },
        { id: 'facility', label: 'Modern Facility', icon: <FaHospital /> },
        { id: 'care', label: 'Patient Care', icon: <FaHeartbeat /> },
        { id: 'accessibility', label: 'Accessibility', icon: <FaHandHoldingHeart /> }
    ];

    const features = {
        expertise: [

            {
                icon: <MdHealthAndSafety className="text-2xl" />,
                title: "Comprehensive Healthcare",
                description: "From primary care to specialized treatments, we offer complete healthcare solutions.",
                stats: "50+ Medical Services",
                color: "bg-emerald-50 border-emerald-100"
            },
            {
                icon: <GiHealing className="text-2xl" />,
                title: "Chronic Disease Management",
                description: "Specialized programs for diabetes, hypertension, and other chronic conditions.",
                stats: "95% Patient Satisfaction",
                color: "bg-purple-50 border-purple-100"
            },
            {
                icon: <FaStethoscope className="text-2xl" />,
                title: "Multidisciplinary Approach",
                description: "Collaborative care with doctors from different specialties for complex cases.",
                stats: "Team-Based Care",
                color: "bg-cyan-50 border-cyan-100"
            }
        ],
        facility: [
            {
                icon: <FaHospital className="text-2xl" />,
                title: "Modern Infrastructure",
                description: "State-of-the-art clinic with advanced medical equipment and comfortable facilities.",
                stats: "Clean & Hygienic",
                color: "bg-green-50 border-green-100"
            },
            {
                icon: <FaAward className="text-2xl" />,
                title: "Accredited Standards",
                description: "Adherence to international healthcare standards and quality protocols.",
                stats: "Quality Certified",
                color: "bg-amber-50 border-amber-100"
            },
            {
                icon: <FaShieldAlt className="text-2xl" />,
                title: "Safe Environment",
                description: "Rigorous infection control measures and patient safety protocols.",
                stats: "Zero Infection Rate",
                color: "bg-red-50 border-red-100"
            },
            {
                icon: <GiMedicines className="text-2xl" />,
                title: "In-House Pharmacy",
                description: "24/7 pharmacy with genuine medicines and affordable pricing.",
                stats: "500+ Medicines Available",
                color: "bg-indigo-50 border-indigo-100"
            }
        ],
        care: [
            {
                icon: <FaHandHoldingHeart className="text-2xl" />,
                title: "Compassionate Care",
                description: "Patient-centered approach with empathy and understanding in every interaction.",
                stats: "98% Patient Feedback Positive",
                color: "bg-pink-50 border-pink-100"
            },
            {
                icon: <FaRegSmile className="text-2xl" />,
                title: "Personalized Treatment",
                description: "Customized treatment plans based on individual patient needs and conditions.",
                stats: "Tailored Healthcare",
                color: "bg-orange-50 border-orange-100"
            },
            {
                icon: <FaUsers className="text-2xl" />,
                title: "Family-Centered Approach",
                description: "Healthcare for all ages, from pediatric to geriatric care under one roof.",
                stats: "All Ages Welcome",
                color: "bg-teal-50 border-teal-100"
            },
            {
                icon: <MdFamilyRestroom className="text-2xl" />,
                title: "Continuity of Care",
                description: "Long-term patient-doctor relationships and consistent follow-up care.",
                stats: "Ongoing Support",
                color: "bg-violet-50 border-violet-100"
            }
        ],
        accessibility: [
            {
                icon: <FaMoneyBillWave className="text-2xl" />,
                title: "Affordable Healthcare",
                description: "Transparent pricing with multiple payment options and insurance acceptance.",
                stats: "Reasonable Pricing",
                color: "bg-lime-50 border-lime-100"
            },
            {
                icon: <TbClock24 className="text-2xl" />,
                title: "Convenient Timings",
                description: "Extended hours with same-day appointments and minimal waiting time.",
                stats: "12-Hour Daily Operation",
                color: "bg-rose-50 border-rose-100"
            },
            {
                icon: <FaAmbulance className="text-2xl" />,
                title: "Emergency Services",
                description: "24/7 emergency care and rapid response team.",
                stats: "Emergency Ready",
                color: "bg-red-50 border-red-100"
            },
        ],
    };

    const testimonials = [
        {
            name: "Alemayehu Bekele",
            role: "Patient",
            quote: "Dr. Asrat Clinic provided excellent care for my diabetes. The doctors are knowledgeable and caring.",
            rating: 5
        },
        {
            name: "Selamawit Tesfaye",
            role: "Mother",
            quote: "My children have been coming here for years. The pediatric care is exceptional and affordable.",
            rating: 5
        },
        {
            name: "Mekonnen Hailu",
            role: "Local Business Owner",
            quote: "The 24/7 emergency service saved my father's life. Quick response and professional care.",
            rating: 5
        }
    ];

    return (
        <section id="why-choose-us" className="py-20 bg-gradient-to-b from-white to-blue-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, margin: "-100px" }}
                    className="text-center mb-16"
                >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-2xl mb-6">
                        <FaRegStar className="text-3xl text-blue-600" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                        Why Choose <span className="text-blue-600">Dr. Asrat Medium Clinic?</span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Experience healthcare excellence with compassion, expertise, and modern facilities 
                        dedicated to your well-being.
                    </p>
                    <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-600 mx-auto rounded-full mt-6"></div>
                </motion.div>

                {/* Tabs Navigation */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    viewport={{ once: true }}
                    className="mb-12"
                >
                    <div className="flex flex-wrap justify-center gap-2 md:gap-3">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all duration-300 ${
                                    activeTab === tab.id
                                        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:shadow-md'
                                }`}
                            >
                                <span className="text-lg">{tab.icon}</span>
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>
                </motion.div>

                {/* Features Grid */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
                    >
                        {features[activeTab].map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                                whileHover={{ 
                                    y: -5,
                                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)"
                                }}
                                className={`relative group rounded-2xl border ${feature.color} p-6 transition-all duration-300 overflow-hidden`}
                            >
                                {/* Icon */}
                                <div className="mb-4">
                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white shadow-md">
                                        <div className="text-blue-600">
                                            {feature.icon}
                                        </div>
                                    </div>
                                </div>

                                {/* Title */}
                                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-700 transition-colors">
                                    {feature.title}
                                </h3>

                                {/* Description */}
                                <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                                    {feature.description}
                                </p>

                                {/* Stats */}
                                <div className="pt-4 border-t border-gray-200">
                                    <div className="text-sm font-semibold text-blue-700">
                                        {feature.stats}
                                    </div>
                                </div>

                                {/* Hover Effect */}
                                <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </motion.div>
                        ))}
                    </motion.div>
                </AnimatePresence>

                {/* Clinic Stats */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    viewport={{ once: true }}
                    className="mb-20"
                >
                    <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-3xl p-8 md:p-12 shadow-2xl">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                            {[
                                { value: "18+", label: "Years of Service", icon: "📅" },
                                { value: "50K+", label: "Patients Treated", icon: "👥" },
                                { value: "25+", label: "Medical Staff", icon: "👨‍⚕️" },
                                { value: "98%", label: "Satisfaction Rate", icon: "⭐" }
                            ].map((stat, index) => (
                                <div key={index} className="p-4">
                                    <div className="text-4xl mb-2">{stat.icon}</div>
                                    <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                                    <div className="text-white/90 text-sm">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Patient Testimonials */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    viewport={{ once: true }}
                    className="mb-16"
                >
                    <div className="text-center mb-12">
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">
                            What Our <span className="text-blue-600">Patients Say</span>
                        </h3>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Real experiences from patients who have trusted us with their healthcare.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {testimonials.map((testimonial, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100"
                            >
                                {/* Rating */}
                                <div className="flex mb-4">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <FaRegStar key={i} className="w-5 h-5 text-yellow-500 fill-current" />
                                    ))}
                                </div>

                                {/* Quote */}
                                <p className="text-gray-700 italic mb-6">
                                    "{testimonial.quote}"
                                </p>

                                {/* Author */}
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-full flex items-center justify-center">
                                        <FaUserCheck className="text-blue-600" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">{testimonial.name}</div>
                                        <div className="text-sm text-gray-600">{testimonial.role}</div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Final Call to Action */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    viewport={{ once: true }}
                    className="text-center"
                >
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-3xl p-8 md:p-12 border border-blue-100 shadow-lg">
                        <div className="max-w-2xl mx-auto">
                            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                                Ready to Experience Quality Healthcare?
                            </h3>
                            <p className="text-gray-700 mb-8 text-lg">
                                Join thousands of satisfied patients who trust Dr. Asrat Medical Clinic 
                                for their healthcare needs.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button className="px-8 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-xl hover:shadow-xl transition-all duration-300">
                                    Book Your First Visit
                                </button>
                                <button className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-xl border-2 border-blue-600 hover:bg-blue-50 transition-colors duration-300">
                                    Contact Our Team
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Trust Indicators */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    viewport={{ once: true }}
                    className="mt-12 text-center"
                >
                    <p className="text-gray-600 mb-6">Trusted by families in Addis Ababa for 18+ years</p>
                    <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
                        <div className="text-gray-500 font-semibold">🏥 Licensed Medical Facility</div>
                        <div className="text-gray-500 font-semibold">📋 Government Certified</div>
                        <div className="text-gray-500 font-semibold">💊 Genuine Medicines</div>
                        <div className="text-gray-500 font-semibold">👨‍⚕️ Qualified Staff</div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default WhyChooseUs;